import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { FilterMatchMode, FilterService } from 'primereact/api';
import { addLocale, locale } from 'primereact/api';
import { Column } from 'primereact/column';
import {
  DataTable,
  DataTableFilterMetaData,
  DataTableSortMeta,
  DataTableStateEvent
} from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';

import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

import DeleteConfirmationDialog from 'ui/shared/DeleteConfirmationDialog';
import TablePageHeader from 'ui/shared/TablePageHeader';

import { ObjectCharacteristicTemplate } from '../constants/ObjectCharacteristicTemplate';
import { isSelected } from '../helpers';
import { useDebounce } from '../hooks';
import useAssetParametersService from '../hooks/services/useAssetParametersService';
import { ruLocale } from '../lang/ruLocale';
import { AssetParameter } from '../types/interfaces';
import ObjectCharacteristicCard from '../ui/ObjectParameterCard';
import { clearFilter } from '../utils';
import VisibilityWrapper from '../utils/wrappers/VisibilityWrapper';

FilterService.register('custom_values', (value, filters) => {
  if (!filters || filters.trim() === '') {
    return true;
  }
  if (!value || !Array.isArray(value)) {
    return false;
  }
  const filterText = filters.toLowerCase().trim();
  return value.some(
    (val) => val.label && val.label.toLowerCase().includes(filterText)
  );
});

export default function AssetParameters() {
  const [filtredObjectCharacteristics, setFiltredObjectCharacteristics] =
    useState<AssetParameter[]>([]);
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [deleteDialogVisible, setDeleteDialogVisible] =
    useState<boolean>(false);
  const [objectCharacteristicToDelete, setObjectCharacteristicToDelete] =
    useState<string | number | null>(null);
  const [selectedObjectCharacteristic, setSelectedObjectCharacteristic] =
    useState<AssetParameter>(ObjectCharacteristicTemplate);
  const [filters, setFilters] = useState<
    Record<string, DataTableFilterMetaData>
  >({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    measureName: { value: null, matchMode: FilterMatchMode.CONTAINS },
    values: { value: null, matchMode: FilterMatchMode.CUSTOM }
  });

  const [loading, setLoading] = useState<boolean>(true);
  const characteristicNameFilterRef = useRef<HTMLInputElement>(null);
  const unitMeasurementFilterRef = useRef<HTMLInputElement>(null);
  const characteristicsValuesFilterRef = useRef<HTMLInputElement>(null);

  const [totalCount, setTotalCount] = useState(1);
  const [multiSortMeta, setMultiSortMeta] = useState<
    DataTableSortMeta[] | undefined | null
  >(undefined);

  const setMultiSortMetaCallback = useCallback((e: DataTableStateEvent) => {
    setLoading(true);
    setMultiSortMeta(e.multiSortMeta);
  }, []);

  const globalFilterRef = useRef<HTMLInputElement>(null);
  const toast = useRef<Toast>(null);
  const objectCharacteristicCardRef = useRef<null | {
    handleCancel: () => void;
  }>(null);

  const isSelectedIdExist = isSelected(selectedObjectCharacteristic);

  addLocale('ru', ruLocale.ru);
  locale('ru');

  const assetParametersService = useAssetParametersService();
  const getAssetParametersCallback = useCallback(() => {
    const sort = multiSortMeta?.find((x) => x !== undefined);
    const nameFilter = filters.name as DataTableFilterMetaData;
    const measureFilter = filters.measureName as DataTableFilterMetaData;
    const valuesFilter = filters.values as DataTableFilterMetaData;
    const globalFilter = filters.global as DataTableFilterMetaData;

    const serviceResult = assetParametersService.getAll({
      sortBy: sort?.field === 'measureName' ? 'measure' : sort?.field,
      sortDirection: sort?.order === 1 ? 'asc' : 'desc',
      filters: JSON.stringify([
        {
          field: 'name',
          value: nameFilter.value,
          operator: nameFilter.matchMode
        },
        {
          field: 'measure',
          value: measureFilter.value,
          operator: measureFilter.matchMode
        },
        {
          field: 'values',
          value: valuesFilter.value,
          operator: valuesFilter.matchMode
        },
        {
          field: 'global',
          value: globalFilter.value,
          operator: globalFilter.matchMode
        }
      ])
    });
    serviceResult.result.then((r) => {
      if (r) {
        const items = r.itemsList.map((x) => ({
          ...x,
          measureName: x.measure?.label ?? ''
        }));
        setFiltredObjectCharacteristics(items);
        setTotalCount((r.total === 0 ? 1 : r.total) ?? 1);
      }
      setLoading(false);
    });
    return () => serviceResult.cancel();
  }, [assetParametersService, filters, multiSortMeta]);

  useEffect(() => {
    const cancel = getAssetParametersCallback();
    return () => cancel();
  }, [getAssetParametersCallback]);

  const onFilterChange = (
    filter: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    const _filters = { ...filters };
    _filters[filter].value = value;
    setFilters(_filters);
  };

  const onFiltersChange = useCallback((e: DataTableStateEvent) => {
    const next = e.filters as Record<string, DataTableFilterMetaData>;
    setFilters(next);
  }, []);

  const { debouncedCallback: debouncedOnFilterChange } = useDebounce(
    onFilterChange,
    300
  );

  const handleInputChange =
    (filter: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedOnFilterChange(filter, e);
    };

  const addButtonHandle = () => {
    setSelectedObjectCharacteristic(ObjectCharacteristicTemplate);
    setDialogVisible(true);
  };

  const editButtonHandle = () => {
    if (toast.current && !isSelectedIdExist) {
      toast.current.show({
        severity: 'warn',
        summary: 'Ошибка',
        detail: 'Выберите характеристику объекта для редактирования',
        life: 3000
      });
    }
    if (isSelectedIdExist) setDialogVisible(true);
  };

  const deleteButtonHandle = () => {
    // If a valid process is selected, render the deletion confirmation dialog.
    if (isSelectedIdExist) {
      setObjectCharacteristicToDelete(selectedObjectCharacteristic.id);
      setDeleteDialogVisible(true);
    }
    if (toast.current && !isSelectedIdExist) {
      toast.current.show({
        severity: 'warn',
        summary: 'Ошибка',
        detail: 'Выберите характеристику объекта для удаления',
        life: 3000
      });
    }
  };

  const handleDeleteConfirmation = () => {
    handleDelete(objectCharacteristicToDelete);
    setDeleteDialogVisible(false);
  };

  const handleUpdate = async (
    id: string | number,
    updatedObject: AssetParameter
  ) => {
    const result =
      await assetParametersService.addOrUpdate(updatedObject)?.result;
    getAssetParametersCallback();
    return result;
  };

  const handleDelete = async (id: string | number | null) => {
    if (id) {
      await assetParametersService.delete(parseInt(id.toString()))?.result;
      setSelectedObjectCharacteristic(ObjectCharacteristicTemplate);
      getAssetParametersCallback();
    }
  };

  const handleAdd = async (assetParameters: AssetParameter) => {
    const result =
      await assetParametersService.addOrUpdate(assetParameters)?.result;
    getAssetParametersCallback();
    return result;
  };

  const closeDialog = () => {
    // Function to close the dialog in parent
    setDialogVisible(false);
  };

  const onHideObjectCharacteristicCard = () => {
    if (objectCharacteristicCardRef.current !== null) {
      objectCharacteristicCardRef.current.handleCancel();
    }
  };

  const header = (
    <TablePageHeader
      tableName="assetParameters"
      onGlobalSearchChange={(value) =>
        handleInputChange('global')({
          target: { value }
        } as React.ChangeEvent<HTMLInputElement>)
      }
      onAdd={addButtonHandle}
      onEdit={editButtonHandle}
      onDelete={deleteButtonHandle}
    />
  );

  const tableElement = document.querySelector('.p-datatable-thead');

  function offset(el: Element) {
    var rect = el.getBoundingClientRect(),
      scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    return { top: rect.top + scrollTop };
  }

  const scrollHeight = useMemo(() => {
    const paginatorElement = document.querySelector('.p-paginator');
    const paginatorHeight =
      paginatorElement instanceof HTMLElement
        ? paginatorElement.offsetHeight
        : 0;
    const tableOffset =
      tableElement instanceof HTMLElement ? offset(tableElement).top : 0;
    return `calc(100vh - ${
      tableElement instanceof HTMLElement ? tableOffset + paginatorHeight : 0
    }px)`;
  }, [tableElement]);

  const characteristicsBodyTemplate = (
    objectData: AssetParameter | undefined
  ) => {
    if (
      !objectData ||
      !objectData.values ||
      !Array.isArray(objectData.values)
    ) {
      console.error('Invalid characteristics data:', objectData);
      return null; // or handle the error accordingly
    }
    return objectData.values.map((characteristic) => (
      <Tag
        style={{ background: 'rgb(225, 234, 245)', color: '#495057' }}
        className="mr-1"
        key={characteristic.id}
      >
        {characteristic.label}
      </Tag>
    ));
  };

  return (
    <div className="assetParameters-table wrapper wrapper--main">
      <Toast ref={toast} />
      <Dialog
        header="Характеристика объекта"
        visible={dialogVisible}
        style={{ minWidth: '600px', width: '600px', minHeight: 'fit-content' }}
        maximizable
        modal
        contentStyle={{ minHeight: 'fit-content' }}
        onHide={() => onHideObjectCharacteristicCard()}
      >
        <VisibilityWrapper code="asset-characteristic-card__roles">
          <ObjectCharacteristicCard
            objectCharacteristicProps={selectedObjectCharacteristic}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
            onAdd={handleAdd}
            closeDialog={closeDialog}
            ref={objectCharacteristicCardRef}
          />
        </VisibilityWrapper>
      </Dialog>

      <DeleteConfirmationDialog
        visible={deleteDialogVisible}
        onHide={() => setDeleteDialogVisible(false)}
        onConfirm={handleDeleteConfirmation}
        entityType="характеристику объекта"
        entityName={selectedObjectCharacteristic?.name}
      />
      <DataTable
        filters={filters}
        lazy
        multiSortMeta={multiSortMeta}
        onSort={setMultiSortMetaCallback}
        selectionMode="single"
        selection={selectedObjectCharacteristic!}
        onSelectionChange={(e) => setSelectedObjectCharacteristic(e.value)}
        onRowDoubleClick={(e) => {
          setDialogVisible(true);
          setSelectedObjectCharacteristic(e.data as AssetParameter);
        }}
        metaKeySelection={false}
        scrollable
        sortMode="multiple"
        removableSort
        scrollHeight={scrollHeight}
        size="small"
        resizableColumns
        reorderableColumns
        reorderableRows
        value={filtredObjectCharacteristics}
        dataKey="id"
        filterDisplay="row"
        loading={loading}
        header={header}
        emptyMessage="Характеристики объектов не найдены. "
        tableStyle={{ minWidth: '50rem' }}
        onFilter={onFiltersChange}
        footer={
          <div
            className="p-paginator p-component p-paginator-bottom justify-content-end"
            style={{
              height: 60
            }}
          >
            <span>{`Всего: ${
              filtredObjectCharacteristics.length > 0 ? totalCount : 0
            }`}</span>
          </div>
        }
        pt={{
          footer: {
            style: {
              padding: 'unset',
              border: 'unset'
            }
          }
        }}
      >
        <Column
          columnKey="name"
          sortable
          field="name"
          header="Название характеристики"
          filter
          onFilterClear={() =>
            clearFilter(
              'name',
              filters,
              setFilters,
              characteristicNameFilterRef
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={characteristicNameFilterRef}
                onChange={handleInputChange('name')}
                placeholder="Поиск по названию характеристики"
              />
            </div>
          }
          style={{ width: '33%' }}
        />
        <Column
          columnKey="measureName"
          sortable
          field="measureName"
          header="Единица измерения"
          filter
          onFilterClear={() =>
            clearFilter(
              'measureName',
              filters,
              setFilters,
              unitMeasurementFilterRef
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={unitMeasurementFilterRef}
                onChange={handleInputChange('measureName')}
                placeholder="Поиск по единицам измерения"
              />
            </div>
          }
          style={{ width: '17%' }}
        />
        <Column
          columnKey="values"
          sortable
          field="values"
          header="Значения характеристик"
          body={characteristicsBodyTemplate}
          filter
          showFilterMatchModes={false}
          onFilterClear={() =>
            clearFilter(
              'values',
              filters,
              setFilters,
              characteristicsValuesFilterRef
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={characteristicsValuesFilterRef}
                onChange={handleInputChange('values')}
                placeholder="Поиск по значениям характеристик"
              />
            </div>
          }
          style={{ width: '50%' }}
        />
      </DataTable>
    </div>
  );
}
